import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import SupportWidget from "@/components/SupportWidget";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { api } from "@/lib/api";
import {
  LayoutDashboard, Kanban, Users, Building2, Briefcase, MailCheck, Package,
  PhoneCall, UserSquare2, BarChart3, Inbox, Settings, LogOut,
  Search, Globe, ShieldCheck, FileBox, ChevronRight, MessageSquare, ClipboardList, Menu, Sparkles,
} from "lucide-react";

const NavItem = ({ to, icon: Icon, label, testid, end }) => (
  <NavLink
    to={to}
    end={end}
    data-testid={testid}
    className={({ isActive }) =>
      `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
        isActive
          ? "bg-[#0052CC] text-white"
          : "text-slate-700 hover:bg-slate-100"
      }`
    }
  >
    <Icon className="w-4 h-4 shrink-0" />
    <span className="truncate">{label}</span>
  </NavLink>
);


export default function Layout() {
  const { user, company, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const searchTimeout = useRef(null);

  useEffect(() => {
    if (!search.trim()) {
      setSearchResults(null);
      return;
    }
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(search)}`);
        setSearchResults(data);
      } catch {
        setSearchResults(null);
      }
    }, 300);
    return () => clearTimeout(searchTimeout.current);
  }, [search]);

  // Heartbeat: tracks active platform time (used for "remaining hours")
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    const beat = async () => {
      if (cancelled || document.hidden) return;
      try { await api.post("/heartbeat?seconds=60"); } catch { /* ignore */ }
    };
    beat();
    const id = setInterval(beat, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user?.id]);

  // Live geolocation broadcast (sales reps only). Every 2 min the browser
  // requests a fresh position and POSTs it so the owner can locate the rep.
  useEffect(() => {
    if (!user?.id || user.role !== "sales_rep") return;
    if (typeof navigator === "undefined" || !navigator.geolocation) return;
    let cancelled = false;
    const push = () => {
      if (cancelled || document.hidden) return;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (cancelled) return;
          api.post("/me/location", {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
          }).catch(() => { /* ignore */ });
        },
        () => { /* user denied / unavailable — silent */ },
        { enableHighAccuracy: false, maximumAge: 60000, timeout: 8000 },
      );
    };
    // First push after 5s (let user get past permission prompt), then every 2min
    const first = setTimeout(push, 5000);
    const id = setInterval(push, 120000);
    return () => { cancelled = true; clearTimeout(first); clearInterval(id); };
  }, [user?.id, user?.role]);

  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const isOwner = user?.role === "company_owner";
  const isSuperAdmin = user?.role === "super_admin";
  const isRep = user?.role === "sales_rep";

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  // Close drawer on navigation
  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);

  return (
    <div className="min-h-screen bg-[#F8FAFC] md:flex">
      {/* Mobile backdrop */}
      {mobileNavOpen && (
        <div
          className="md:hidden fixed inset-0 bg-slate-900/40 z-30"
          onClick={() => setMobileNavOpen(false)}
          data-testid="mobile-nav-backdrop"
        />
      )}
      {/* Sidebar */}
      <aside
        className={`w-64 bg-white border-r border-slate-200 flex flex-col fixed inset-y-0 left-0 z-40 transition-transform md:translate-x-0 ${mobileNavOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}
        data-testid="sidebar"
      >
        <div className="px-5 py-5 border-b border-slate-200">
          <Link to="/app" className="flex items-center gap-2" data-testid="brand-link">
            <div className="w-9 h-9 rounded-md bg-[#0052CC] flex items-center justify-center text-white font-bold tracking-tight">
              GE
            </div>
            <div className="flex flex-col">
              <span className="font-heading text-base font-bold text-slate-900 leading-tight">
                {t("app.name")}
              </span>
              <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                Export CRM
              </span>
            </div>
          </Link>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {isSuperAdmin && (
            <>
              <div className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">
                {t("nav.platform")}
              </div>
              <NavItem to="/app/admin" icon={ShieldCheck} label="Platform Admin" testid="nav-admin" end />
              <NavItem to="/app/admin/partners" icon={UserSquare2} label="Partners" testid="nav-admin-partners" />
              <NavItem to="/app/ai-leads" icon={Sparkles} label="AI Lead Finder" testid="nav-admin-ai-leads" />
              <NavItem to="/app/leads/agency" icon={Briefcase} label="Agency Workbench" testid="nav-admin-agency" />
              <NavItem to="/app/email-agent" icon={MailCheck} label="Email Agent" testid="nav-admin-email-agent" />
            </>
          )}

          {(isOwner || isRep) && (
            <NavItem to="/app" icon={LayoutDashboard} label={t("nav.dashboard")} testid="nav-dashboard" end />
          )}

          {isRep && (
            <>
              <NavItem to="/app/work-report" icon={ClipboardList} label={t("nav.work_report")} testid="nav-work-report" />
              <NavItem to="/app/contacts" icon={Users} label={t("nav.customers")} testid="nav-contacts" />
              <NavItem to="/app/calls" icon={PhoneCall} label={t("nav.calls")} testid="nav-calls" />
              <NavItem to="/app/chat" icon={MessageSquare} label={t("nav.chat_owner")} testid="nav-chat" />
            </>
          )}

          {isOwner && (
            <>
              <NavItem to="/app/owner/reports" icon={BarChart3} label={t("nav.owner_reports")} testid="nav-owner-reports" />
              <NavItem to="/app/owner/calls-emails" icon={PhoneCall} label={t("nav.owner_calls_emails")} testid="nav-owner-calls" />
              <NavItem to="/app/work-report" icon={ClipboardList} label={t("nav.work_report")} testid="nav-owner-work-report" />
              <NavItem to="/app/owner/chat" icon={MessageSquare} label={t("nav.owner_chat")} testid="nav-owner-chat" />
              <div className="px-2 pt-3 pb-1 text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">
                Company
              </div>
              <NavItem to="/app/products" icon={Package} label={t("nav.products")} testid="nav-products" />
              <NavItem to="/app/sales-reps" icon={UserSquare2} label={t("nav.sales_reps")} testid="nav-sales-reps" />
              <NavItem to="/app/inquiries" icon={Inbox} label={t("nav.inquiries")} testid="nav-inquiries" />
              <NavItem to="/app/ai-leads" icon={Sparkles} label="AI Lead Finder" testid="nav-ai-leads" />
              <NavItem to="/app/profile" icon={Globe} label={t("nav.company_profile")} testid="nav-profile" />
            </>
          )}

          <div className="pt-4 mt-4 border-t border-slate-200">
            <NavItem to="/app/settings" icon={Settings} label={t("nav.settings")} testid="nav-settings" />
          </div>
        </nav>

        <div className="px-3 py-3 border-t border-slate-200">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center text-sm font-semibold text-slate-700">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-900 truncate" data-testid="user-name">
                {user?.name}
              </div>
              <div className="text-xs text-slate-500 truncate">
                {user?.role?.replace("_", " ")}
              </div>
            </div>
            <button
              data-testid="logout-button"
              onClick={handleLogout}
              className="p-2 rounded-md hover:bg-slate-100 text-slate-500"
              title={t("auth.logout")}
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 md:ml-64 min-w-0">
        {/* Header */}
        <header className="sticky top-0 z-20 backdrop-blur-xl bg-white/85 border-b border-slate-200">
          <div className="px-4 md:px-8 py-3 flex items-center gap-3">
            {/* Mobile hamburger */}
            <button
              type="button"
              onClick={() => setMobileNavOpen(true)}
              className="md:hidden p-2 -ml-2 rounded-md hover:bg-slate-100 text-slate-700"
              data-testid="mobile-nav-toggle"
              aria-label="Open menu"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="relative flex-1 max-w-xl">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                data-testid="global-search-input"
                type="text"
                placeholder={t("search.placeholder")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 text-sm border border-slate-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-[#0052CC]/40 focus:border-[#0052CC]"
              />
              {searchResults && (
                <div className="absolute mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg z-50 max-h-96 overflow-y-auto" data-testid="global-search-results">
                  {Object.entries(searchResults).map(([k, items]) => (
                    items.length > 0 && (
                      <div key={k} className="p-2">
                        <div className="px-2 py-1 text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">
                          {k.replace("_", " ")}
                        </div>
                        {items.map((it) => (
                          <div key={it.id} className="px-3 py-1.5 text-sm hover:bg-slate-50 rounded cursor-default">
                            {it.first_name ? `${it.first_name} ${it.last_name || ""}` : it.name || it.title}
                          </div>
                        ))}
                      </div>
                    )
                  ))}
                  {Object.values(searchResults).every((v) => v.length === 0) && (
                    <div className="px-4 py-3 text-sm text-slate-500">{t("common.no_data")}</div>
                  )}
                </div>
              )}
            </div>
            <LanguageSwitcher />
          </div>
        </header>

        <main className="px-4 md:px-8 py-6">
          <ErrorBoundary key={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
      <SupportWidget />
    </div>
  );
}
